import { FC, useMemo } from 'react';

import clsx from 'clsx';
import flatMapDeep from 'lodash-es/flatMapDeep';
import uniqBy from 'lodash-es/uniqBy';

import { MenuItemChild } from '@da-g3-acl/shared/common';

import { InfoIcon } from '../../icons/InfoIcon';
import { StatusBarMenu, StatusBarMenuProps } from '../StatusBarMenu';

import styles from './index.module.scss';

export interface StatusBarMenuSelectorProps extends Pick<StatusBarMenuProps, 'maxHeight'> {
	selectionTypeName: string;
	infoTooltipContent?: string;
	selectedMenuItemIds: string[] | undefined;
	menuItems: MenuItemChild[];
	onTypeSelected?: (id: string) => void;
	isLoading?: boolean | undefined;
}

export const StatusBarMenuSelector: FC<StatusBarMenuSelectorProps> = ({
	selectionTypeName,
	selectedMenuItemIds,
	menuItems,
	infoTooltipContent,
	isLoading,
	onTypeSelected,
	maxHeight,
}: StatusBarMenuSelectorProps) => {
	const { selected, showTags } = useMemo(() => {
		const flattenedItems = [...menuItems, ...flatMapDeep(menuItems, (m) => m.children ?? [])];

		const showTags = uniqBy(flattenedItems, (it) => it.tag)?.length > 1;
		const selected = flattenedItems?.filter((it) => selectedMenuItemIds?.includes(it.id));

		return { showTags, selected };
	}, [menuItems, selectedMenuItemIds]);

	return (
		<div className={styles.menuSelectionGroup} data-statusbar-item-type={`menu-${selectionTypeName}-selector`}>
			{(menuItems?.length ?? 0) === 0 ? (
				<div className={styles.emptyTitle}>No {selectionTypeName}</div>
			) : (
				<StatusBarMenu
					selectedMenuItemIds={selectedMenuItemIds}
					menuItems={menuItems}
					showTags={showTags}
					onItemSelected={(id) => onTypeSelected && id && onTypeSelected(id)}
					defaultTitle={selectionTypeName}
					maxHeight={maxHeight}
				/>
			)}
			<span className={clsx(styles.loader, { [styles.isLoading]: isLoading })}></span>
			<div className={styles.imageType}>
				<div className={styles.selectionText}>
					{selected?.length > 1
						? `${selected[0].name} (+${selected.length - 1})`
						: selected?.find(() => true)?.name}
				</div>
				{infoTooltipContent && (
					<span className={styles.tooltip}>
						<InfoIcon />
						<span className={styles.popoverText}>{infoTooltipContent}</span>
					</span>
				)}
			</div>
		</div>
	);
};
